/**
 * Data + mutations for Costs & Margin tab.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createJobCost,
  createSubChangeOrder,
  deleteJobCost,
  fetchCostsMargin,
  fetchJobCosts,
  setSubcontractAmount,
  updateJobCost,
  type JobCostRow,
} from '../../../../api/costsMargin'
import { fetchProjectVendors } from '../../../../api/projects'
import { useActionToast } from '../../../../lib/useActionToast'
import { buildQuestionSequence, subDisplayName } from '../../../../lib/costsMarginQuestions'
import { loadProgress, saveProgress } from '../../../../lib/costsMarginProgress'
import type { QuestionSavePayload } from './types'

export function useCostsMarginData(projectId: number) {
  const qc = useQueryClient()
  const { showToast, Toast } = useActionToast()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [skippedIds, setSkippedIds] = useState<string[]>([])
  const [savedFlash, setSavedFlash] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<JobCostRow | null>(null)
  const [editCost, setEditCost] = useState<JobCostRow | null>(null)

  const marginQ = useQuery({
    queryKey: ['costs-margin', projectId],
    queryFn: () => fetchCostsMargin(projectId),
    staleTime: 15_000,
  })
  const costsQ = useQuery({
    queryKey: ['job-costs', projectId],
    queryFn: () => fetchJobCosts(projectId),
    staleTime: 15_000,
  })
  const vendorsQ = useQuery({
    queryKey: ['project-vendors', projectId],
    queryFn: () => fetchProjectVendors(projectId),
    staleTime: 30_000,
  })

  const questions = useMemo(() => {
    const fromMargin = (marginQ.data?.commitments ?? []).map((c) => ({
      projectVendorId: c.project_vendor_id,
      displayName: subDisplayName({ company_name: c.company_name, trade: c.trade }),
    }))
    if (fromMargin.length > 0) return buildQuestionSequence(fromMargin)
    return buildQuestionSequence(
      (vendorsQ.data ?? []).map((v) => ({
        projectVendorId: v.id,
        displayName: subDisplayName({ company_name: v.company_name, name: v.name }),
      })),
    )
  }, [marginQ.data, vendorsQ.data])

  useEffect(() => {
    const saved = loadProgress(projectId)
    if (!saved) return
    setSkippedIds(saved.skippedIds)
    setCurrentIndex(Math.min(saved.currentIndex, Math.max(0, questions.length - 1)))
  }, [projectId, questions.length])

  useEffect(() => {
    saveProgress(projectId, { currentIndex, skippedIds })
  }, [projectId, currentIndex, skippedIds])

  const invalidate = useCallback(async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['costs-margin', projectId] }),
      qc.invalidateQueries({ queryKey: ['job-costs', projectId] }),
    ])
  }, [qc, projectId])

  const flashSaved = () => {
    setSavedFlash(true)
    window.setTimeout(() => setSavedFlash(false), 1800)
  }

  const saveMut = useMutation({
    mutationFn: async (payload: QuestionSavePayload) => {
      if (payload.kind === 'subcontract_amount') {
        return setSubcontractAmount(projectId, payload.projectVendorId, payload.amount_cents)
      }
      if (payload.kind === 'job_cost') return createJobCost(projectId, payload)
      return createSubChangeOrder(projectId, payload)
    },
    onSuccess: async () => { flashSaved(); showToast('Saved'); await invalidate() },
    onError: (err: Error) => showToast(err.message || 'Could not save'),
  })

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteJobCost(projectId, id),
    onSuccess: async () => {
      showToast('Item deleted'); setDeleteTarget(null); await invalidate()
    },
    onError: (err: Error) => showToast(err.message || 'Could not delete'),
  })

  const vendorNames = useMemo(() => {
    const m = new Map<number, string>()
    for (const c of marginQ.data?.commitments ?? []) {
      if (c.vendor_id != null) {
        m.set(c.vendor_id, subDisplayName({ company_name: c.company_name, trade: c.trade }))
      }
    }
    return m
  }, [marginQ.data])

  return {
    showToast, Toast, questions, currentIndex, setCurrentIndex, skippedIds, setSkippedIds,
    savedFlash, deleteTarget, setDeleteTarget, editCost, setEditCost,
    margin: marginQ.data, costs: costsQ.data ?? [], saveMut, deleteMut, vendorNames,
    flashSaved, invalidate, updateJobCost: (id: number, patch: Partial<JobCostRow>) =>
      updateJobCost(projectId, id, patch as never),
  }
}
